import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { useAuth } from '../components/AuthContext';
import { useCart } from '../contexts/CartContext';
import BuyerQuickRegistration from '../components/BuyerQuickRegistration';
import ProductDetailModal from '../components/ProductDetailModal';
import Cart, { CartFab } from '../components/Cart';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  InputAdornment,
  IconButton // Ajout de IconButton pour les boutons de navigation
} from '@mui/material';
import { Search, FilterList, LocationOn, Category, ChevronLeft, ChevronRight } from '@mui/icons-material'; // Ajout des icônes de navigation

const SalesPage = () => {
  // Définir la constante PLACEHOLDER_URL une seule fois au début du composant
  const PLACEHOLDER_URL = 'https://via.placeholder.com/400x200?text=Produit+Agricole';
  
  const { isBuyer, isAuthenticated, userRole, currentUser } = useAuth();
  const { totalItems } = useCart();
  const [announcements, setAnnouncements] = useState([]);
  const [filteredAnnouncements, setFilteredAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    location: '',
    minPrice: '',
    maxPrice: ''
  });
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  
  // États pour le modal de détails du produit et le panier
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCart, setShowCart] = useState(false);

  // Ajout d'un état pour suivre l'index de l'image affichée pour chaque annonce
  const [currentImageIndexes, setCurrentImageIndexes] = useState({});

  // Fonction pour naviguer vers l'image précédente
  const handlePrevImage = (announcementId) => {
    setCurrentImageIndexes(prev => {
      const currentIndex = prev[announcementId] || 0;
      const announcement = announcements.find(a => a.id === announcementId);
      const imageCount = announcement?.imageUrls?.length || 0;
      return {
        ...prev,
        [announcementId]: currentIndex > 0 ? currentIndex - 1 : imageCount - 1
      };
    });
  };

  // Fonction pour gérer le pluriel des unités
  const getPluralUnit = (quantity, unit) => {
    if (!unit) return '';
    
    const qty = parseInt(quantity);
    if (qty <= 1) return unit;
    
    // Gestion spécifique pour les unités courantes
    switch (unit.toLowerCase()) {
      case 'sac':
        return 'sacs';
      case 'caisse':
        return 'caisses';
      case 'kg':
      case 'kilogramme':
        return unit; // kg reste invariable
      case 'tonne':
        return 'tonnes';
      case 'litre':
        return 'litres';
      case 'pièce':
        return 'pièces';
      default:
        return unit + 's'; // Ajouter 's' par défaut
    }
  };
  
  // Fonction pour naviguer vers l'image suivante
  const handleNextImage = (announcementId) => {
    setCurrentImageIndexes(prev => {
      const currentIndex = prev[announcementId] || 0;
      const announcement = announcements.find(a => a.id === announcementId);
      const imageCount = announcement?.imageUrls?.length || 0;
      return {
        ...prev,
        [announcementId]: (currentIndex + 1) % imageCount
      };
    });
  };

  // Fonction pour gérer le clic sur le bouton Commander
  const handleOrderClick = (announcement) => {
    if (!isAuthenticated) {
      // Si l'utilisateur n'est pas connecté, ouvrir le modal d'inscription
      setShowRegistrationModal(true);
    } else if (!isBuyer) {
      // Messages spécifiques selon le rôle de l'utilisateur
      let errorMessage = '';
      if (userRole === 'farmer') {
        errorMessage = `Vous êtes actuellement connecté en tant qu'agriculteur (${currentUser?.firstName || ''} ${currentUser?.lastName || ''}). Pour passer des commandes, vous devez créer un compte acheteur séparé.`;
      } else if (userRole === 'admin' || userRole === 'PDG') {
        errorMessage = `Vous êtes actuellement connecté en tant qu'${userRole === 'admin' ? 'administrateur' : 'PDG'} (${currentUser?.firstName || currentUser?.displayName || currentUser?.email}). Pour passer des commandes, vous devez créer un compte acheteur séparé.`;
      } else {
        errorMessage = 'Seuls les acheteurs peuvent passer des commandes. Veuillez vous inscrire en tant qu\'acheteur.';
      }
      setError(errorMessage);
    } else {
      // Si l'utilisateur est un acheteur connecté, procéder à la commande
      handleProceedToOrder(announcement);
    }
  };

  // Fonction pour procéder à la commande - ouvrir le modal de détails du produit
  const handleProceedToOrder = (announcement) => {
    setSelectedProduct(announcement);
    setShowProductModal(true);
  };

  // Fonctions pour gérer le modal de détails du produit
  const handleCloseProductModal = () => {
    setShowProductModal(false);
    setSelectedProduct(null);
  };

  const handleContinueShopping = () => {
    setShowProductModal(false);
    setSelectedProduct(null);
  };

  // Fonctions pour gérer le panier
  const handleOpenCart = () => {
    setShowCart(true);
  };

  const handleCloseCart = () => {
    setShowCart(false);
  };

  const handleCheckout = () => {
    // Ici vous pouvez implémenter la logique de checkout
    // Par exemple, rediriger vers une page de paiement
    console.log('Procéder au paiement');
    // Exemple: navigate('/checkout');
  };

  // Fonction appelée après une inscription réussie
  const handleRegistrationSuccess = () => {
    setShowRegistrationModal(false);
    // L'utilisateur sera redirigé vers la page de vérification d'email
  };

  // Fonction pour fermer le modal d'inscription
  const handleCloseRegistrationModal = () => {
    setShowRegistrationModal(false);
  };

  // Récupérer uniquement les annonces approuvées
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setLoading(true);
        const announcementsQuery = query(
          collection(db, 'announcements'),
          where('status', '==', 'approved'), // Filtrer uniquement les annonces approuvées
          orderBy('createdAt', 'desc')
        );
        
        const announcementsSnapshot = await getDocs(announcementsQuery);
        const announcementsList = announcementsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));
        
        setAnnouncements(announcementsList);
        setFilteredAnnouncements(announcementsList);
        
        // Extraire les catégories et emplacements uniques pour les filtres
        const uniqueCategories = [...new Set(announcementsList.map(item => item.category).filter(Boolean))];
        const uniqueLocations = [...new Set(announcementsList.map(item => item.location).filter(Boolean))];
        
        setCategories(uniqueCategories);
        setLocations(uniqueLocations);
      } catch (err) {
        console.error('Erreur lors de la récupération des annonces:', err);
        setError('Erreur lors de la récupération des annonces');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAnnouncements();
  }, []);

  // Filtrer les annonces en fonction des critères
  useEffect(() => {
    let results = [...announcements];
    
    // Filtre par terme de recherche
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      results = results.filter(item => 
        item.title?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.farmName?.toLowerCase().includes(searchLower) ||
        item.farmerName?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filtre par catégorie
    if (filters.category) {
      results = results.filter(item => item.category === filters.category);
    }
    
    // Filtre par emplacement
    if (filters.location) {
      results = results.filter(item => item.location === filters.location);
    }
    
    // Filtre par prix avec validation améliorée
    const minPrice = filters.minPrice ? parseFloat(filters.minPrice) : null;
    const maxPrice = filters.maxPrice ? parseFloat(filters.maxPrice) : null;
    
    if (minPrice !== null && !isNaN(minPrice)) {
      results = results.filter(item => {
        const itemPrice = parseFloat(item.price);
        return !isNaN(itemPrice) && itemPrice >= minPrice;
      });
    }
    
    if (maxPrice !== null && !isNaN(maxPrice)) {
      results = results.filter(item => {
        const itemPrice = parseFloat(item.price);
        return !isNaN(itemPrice) && itemPrice <= maxPrice;
      });
    }
    
    // Filtre par plage de prix si les deux sont définis
    if (minPrice !== null && maxPrice !== null && !isNaN(minPrice) && !isNaN(maxPrice)) {
      results = results.filter(item => {
        const itemPrice = parseFloat(item.price);
        return !isNaN(itemPrice) && itemPrice >= minPrice && itemPrice <= maxPrice;
      });
    }
    
    setFilteredAnnouncements(results);
  }, [announcements, searchTerm, filters]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    
    // Validation pour les champs de prix
    if (name === 'minPrice' || name === 'maxPrice') {
      // Permettre seulement les nombres positifs
      if (value !== '' && (isNaN(value) || parseFloat(value) < 0)) {
        return; // Ignorer la saisie invalide
      }
    }
    
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [name]: value
      };
      
      // Validation croisée des prix min/max
      if (name === 'minPrice' && newFilters.maxPrice !== '') {
        const minPrice = parseFloat(value || 0);
        const maxPrice = parseFloat(newFilters.maxPrice);
        if (minPrice > maxPrice) {
          // Ajuster automatiquement le prix max si nécessaire
          newFilters.maxPrice = value;
        }
      }
      
      if (name === 'maxPrice' && newFilters.minPrice !== '') {
        const minPrice = parseFloat(newFilters.minPrice);
        const maxPrice = parseFloat(value || 0);
        if (maxPrice < minPrice && value !== '') {
          // Ajuster automatiquement le prix min si nécessaire
          newFilters.minPrice = value;
        }
      }
      
      return newFilters;
    });
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilters({
      category: '',
      location: '',
      minPrice: '',
      maxPrice: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Approuvée';
      case 'pending':
        return 'En attente';
      case 'rejected':
        return 'Rejetée';
      default:
        return 'Inconnu';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      <Typography variant="h4" component="h1" gutterBottom sx={{ 
        color: '#014527',
        fontWeight: 'bold',
        textAlign: 'center',
        mb: 4,
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -10,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '80px',
          height: '4px',
          backgroundColor: '#28a745',
          borderRadius: '2px'
        }
      }}>
        Marché des Produits Agricoles
      </Typography>
      
      {/* Section de filtrage */}
      <Paper elevation={3} sx={{ 
        p: 3, 
        mb: 4, 
        borderRadius: '10px',
        backgroundColor: 'white'
      }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Rechercher"
              value={searchTerm}
              onChange={handleSearchChange}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    name="category"
                    value={filters.category}
                    onChange={handleFilterChange}
                    label="Catégorie"
                    startAdornment={
                      <InputAdornment position="start">
                        <Category />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">Toutes les catégories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Localisation</InputLabel>
                  <Select
                    name="location"
                    value={filters.location}
                    onChange={handleFilterChange}
                    label="Localisation"
                    startAdornment={
                      <InputAdornment position="start">
                        <LocationOn />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">Toutes les localisations</MenuItem>
                    {locations.map((location) => (
                      <MenuItem key={location} value={location}>
                        {location}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Prix min"
                      name="minPrice"
                      type="number"
                      value={filters.minPrice}
                      onChange={handleFilterChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">FCFA</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Prix max"
                      name="maxPrice"
                      type="number"
                      value={filters.maxPrice}
                      onChange={handleFilterChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">FCFA</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={resetFilters}
              startIcon={<FilterList />}
            >
              Réinitialiser les filtres
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Résultats */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          {filteredAnnouncements.length} annonce(s) trouvée(s)
        </Typography>
        <Divider />
      </Box>
      
      {filteredAnnouncements.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: '10px' }}>
          <Typography variant="h6" color="text.secondary">
            Aucune annonce ne correspond à vos critères de recherche.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={resetFilters}
            sx={{ mt: 2 }}
          >
            Réinitialiser les filtres
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredAnnouncements.map((announcement) => {
            // Récupérer l'index de l'image actuelle pour cette annonce
            const currentImageIndex = currentImageIndexes[announcement.id] || 0;
            // Vérifier si l'annonce a plusieurs images
            const hasMultipleImages = announcement.imageUrls && announcement.imageUrls.length > 1;
            
            return (
              <Grid item xs={12} sm={6} md={4} key={announcement.id}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)' }
                }}>
                  {/* Section de l'image avec boutons de navigation */}
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="140"
                      image={announcement.imageUrls && announcement.imageUrls.length > 0 
                        ? announcement.imageUrls[currentImageIndex] 
                        : PLACEHOLDER_URL}
                      alt={announcement.title}
                      sx={{
                        objectFit: 'cover'
                      }}
                    />
                    
                    {/* Boutons de navigation (affichés uniquement si plusieurs images) */}
                    {hasMultipleImages && (
                      <>
                        <IconButton 
                          size="small" 
                          onClick={() => handlePrevImage(announcement.id)}
                          sx={{ 
                            position: 'absolute', 
                            left: 5, 
                            top: '50%', 
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(255,255,255,0.7)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                          }}
                        >
                          <ChevronLeft />
                        </IconButton>
                        
                        <IconButton 
                          size="small" 
                          onClick={() => handleNextImage(announcement.id)}
                          sx={{ 
                            position: 'absolute', 
                            right: 5, 
                            top: '50%', 
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(255,255,255,0.7)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                          }}
                        >
                          <ChevronRight />
                        </IconButton>
                        
                        {/* Indicateur du nombre d'images */}
                        <Box sx={{ 
                          position: 'absolute', 
                          bottom: 5, 
                          right: 5, 
                          bgcolor: 'rgba(0,0,0,0.5)', 
                          color: 'white',
                          px: 1,
                          borderRadius: 1,
                          fontSize: '0.75rem'
                        }}>
                          {currentImageIndex + 1}/{announcement.imageUrls.length}
                        </Box>
                      </>
                    )}
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="h2" gutterBottom>
                        {announcement.title}
                      </Typography>
                      <Chip 
                        size="small" 
                        label={getStatusText(announcement.status)} 
                        color={getStatusColor(announcement.status)}
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, height: '60px', overflow: 'hidden' }}>
                      {announcement.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">
                        <strong>Prix:</strong> {announcement.price} FCFA/{announcement.unit}
                      </Typography>
                      <Typography variant="body2">
    <strong>Disponible:</strong> {announcement.quantity} {getPluralUnit(announcement.quantity, announcement.unit)}
  </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOn fontSize="small" color="action" sx={{ mr: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">
                        {announcement.location || 'Non spécifié'}
                      </Typography>
                    </Box>
                  </CardContent>
                  
                  <CardActions>
                    <Button 
                      size="small" 
                      variant="contained" 
                      color="primary" 
                      fullWidth
                      onClick={() => handleOrderClick(announcement)}
                    >
                      Commander
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
      
      {/* Modal d'inscription rapide pour les acheteurs */}
      <BuyerQuickRegistration
        open={showRegistrationModal}
        onClose={handleCloseRegistrationModal}
        onSuccess={handleRegistrationSuccess}
      />
      
      {/* Modal de détails du produit */}
      <ProductDetailModal
        open={showProductModal}
        onClose={handleCloseProductModal}
        product={selectedProduct}
        onContinueShopping={handleContinueShopping}
      />
      
      {/* Panier */}
      <Cart
        open={showCart}
        onClose={handleCloseCart}
        onCheckout={handleCheckout}
      />
      
      {/* Bouton flottant du panier */}
      <CartFab onClick={handleOpenCart} />
    </Container>
  );
};

export default SalesPage;